import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Alert,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Stack,
} from '@mui/material';
import { Add, ExpandMore, CheckCircle, LocalShipping, Payment, LocationOn, Edit, ArrowForward, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCart } from '../../hooks/storefront/useGetCart';
import { useGetCustomerAddresses } from '../../hooks/customer/useGetCustomerAddresses';
import { useCreateCustomerAddress } from '../../hooks/customer/useCreateCustomerAddress';
import AddressesList from '../../components/customer/AddressesList';
import AddressForm from '../../components/customer/AddressForm';
import Btn from '../../components/global/Btn';
import transport from '../../utils/axios';
import { PUBLIC_URL } from '../../config';
import { trackPaymentFailure } from '../../utils/telemetry';
import { toast } from 'react-hot-toast';
import { formatMoney } from '../../utils/money';
import GuestDetailsCard from '../../components/customer/GuestDetailsCard';
import { useAuthContext } from '../../auth/useAuthContext';

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();

  // ---------------------------------------------------------------
  // Step state (Sprint 3.1)
  //
  // Three logical steps drive both the top Stepper UI and the
  // accordion's `expanded` value below. The accordion is kept so
  // shipping is still a Radio group inside it (no need to rebuild
  // 400 lines of form markup) — `expanded` is now derived, not
  // independent. Free clicking on a stepper step jumps to it.
  // ---------------------------------------------------------------
  const STEPS = ['address', 'shipping', 'payment'];
  const [activeStep, setActiveStep] = useState(0);
  const expanded = STEPS[activeStep];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      const idx = STEPS.indexOf(panel);
      if (idx >= 0) setActiveStep(idx);
    }
  };

  // Data Hooks
  const { cartData: cart, isLoading: isCartLoading } = useGetCart();
  const { data: addresses, isLoading: isAddressLoading, refetch: refetchAddresses } = useGetCustomerAddresses();
  const createAddressHook = useCreateCustomerAddress();
  const { isAuthenticated } = useAuthContext();

  // Guest details — only used when isAuthenticated is false. The
  // backend creates a User row from these on place-order (Backend
  // Sprint 7) and emails a password-set link.
  const [guestDetails, setGuestDetails] = useState({ name: '', email: '', phone: '' });
  const [guestErrors, setGuestErrors] = useState({});

  // Aborts in-flight checkout requests if the component unmounts mid-submit
  // (user navigated away). Without this the request would complete in the
  // background, possibly creating a server-side order the customer never
  // sees, and React would warn about state updates on an unmounted component.
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Warn the customer before they close the tab while a submit is mid-flight.
  // The window between place-order success and the Paymob handoff is the
  // worst time to lose the page: we'd leave a pending order behind and the
  // customer wouldn't know to come back to /orders to finish payment.
  // We use a ref (not isPlacingOrder directly) so we can flip it off
  // synchronously right before our own redirect to Paymob — otherwise the
  // browser would prompt on every legitimate handoff.
  const warnUnloadRef = useRef(false);
  useEffect(() => {
    const handler = (e) => {
      if (!warnUnloadRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Local State
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('paymob');
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [notes] = useState('');

  // Sprint A — when /payments/start fails because the gateway (Paymob)
  // isn't configured on the server, offer COD as a fallback on the same
  // pending order instead of orphaning it. Stores the order id so the
  // dialog's "Try COD" action can call /payments/start again with
  // provider='cod' against it.
  const [gatewayFallbackOrderId, setGatewayFallbackOrderId] = useState(null);
  const [isFallbackSubmitting, setIsFallbackSubmitting] = useState(false);

  // Fetch shipping methods when address is selected
  useEffect(() => {
    if (selectedAddressId) {
      transport.get('/checkout/shipping-methods', { params: { address_id: selectedAddressId } })
        .then(res => {
          setShippingMethods(res.data.data || []);
          if (res.data.data && res.data.data.length > 0) {
            setSelectedShippingMethod(res.data.data[0].id);
          }
        })
        .catch(err => console.error('Failed to load shipping methods', err));
    }
  }, [selectedAddressId]);

  // Set default address on load
  useEffect(() => {
    if (addresses && addresses.length > 0 && !selectedAddressId) {
      const defaultAddr = addresses.find(a => a.is_default_shipping) || addresses[0];
      setSelectedAddressId(defaultAddr.id);
    }
  }, [addresses, selectedAddressId]);

  const handleNextToShipping = () => {
    if (!selectedAddressId) {
      toast.error(t('checkout.selectAddressError', 'Please select a shipping address'));
      return;
    }
    setActiveStep(1);
  };

  const handleNextToPayment = () => {
    if (!selectedShippingMethod) {
      toast.error(t('checkout.selectShippingError', 'Please select a shipping method'));
      return;
    }
    setActiveStep(2);
  };

  const handleAddressCreate = (data) => {
    createAddressHook.mutate(data, {
      onSuccess: () => {
        setShowAddressForm(false);
        toast.success(t('profile.addressAdded', 'Address added successfully'));
        refetchAddresses();
      },
      onError: () => {
        toast.error(t('profile.addressAddError', 'Failed to add address'));
      }
    });
  };

  const validateGuestDetails = () => {
    if (isAuthenticated) return true;
    const errors = {};
    if (!guestDetails.name?.trim()) errors.name = t('checkout.nameRequired', 'Name is required');
    if (!guestDetails.email?.trim()) errors.email = t('checkout.emailRequired', 'Email is required');
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guestDetails.email.trim())) {
      errors.email = t('checkout.emailInvalid', 'Enter a valid email');
    }
    if (!guestDetails.phone?.trim()) errors.phone = t('checkout.phoneRequired', 'Phone is required');
    setGuestErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Surfaces the rate-limit error from the backend, preferring the
  // server-provided retry_after seconds when available so the user gets
  // a precise wait rather than a generic "try again in a minute".
  const showThrottleToast = (err) => {
    const retryAfter = Number(err?.response?.data?.meta?.retry_after);
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      toast.error(t('checkout.throttleSeconds', { n: retryAfter }));
    } else {
      toast.error(t('checkout.throttle'));
    }
  };

  // Allow-list of payment provider domains we will redirect the browser to.
  // Anything outside is treated as untrusted and the user goes to the result
  // page instead — protects against an open-redirect via a tampered server
  // response or a misconfigured provider driver.
  const isTrustedRedirect = (urlStr) => {
    try {
      const u = new URL(urlStr);
      if (u.protocol !== 'https:') return false;
      const allowed = [
        'accept.paymobsolutions.com',
        'accept.paymob.com',
        'pay.paymob.com',
        'oneclick.paymob.com',
      ];
      return allowed.some((host) => u.hostname === host || u.hostname.endsWith('.' + host));
    } catch {
      return false;
    }
  };

  // Sprint A — extract a user-facing message from a failed /payments/start
  // response. Backend Sprint A puts the gateway error under errors.gateway
  // (e.g. "Paymob is not configured. Please contact support."), but for older
  // failure shapes we still fall back to the top-level `message`.
  const extractGatewayError = (body) => {
    const g = body?.errors?.gateway;
    if (typeof g === 'string' && g) return g;
    if (Array.isArray(g) && g[0]) return g[0];
    return body?.message || null;
  };

  // Two-step flow per backend brief:
  //   1) POST /checkout/place-order  → creates a pending order (no payment)
  //   2) POST /payments/start        → starts the provider flow, returns redirect_url
  // The order can outlive a failed payment attempt, so we never bundle
  // provider into place-order.
  const handlePlaceOrder = async () => {
    if (isPlacingOrder) return; // hard re-entry guard
    if (!validateGuestDetails()) {
      toast.error(t('checkout.fixGuestErrors', 'Please fill in your contact details'));
      return;
    }
    setIsPlacingOrder(true);
    warnUnloadRef.current = true;

    // Fresh AbortController per submit so the unmount cleanup only kills
    // *this* attempt and a retry isn't pre-aborted.
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    // Idempotency key — same key for any retry of THIS submission, so the
    // backend can dedupe if the network retried mid-flight. Backend must
    // honor the `Idempotency-Key` header on /checkout/place-order.
    const idemKey = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

    const orderPayload = {
      shipping_address_id: selectedAddressId,
      billing_address_id: selectedAddressId,
      shipping_method_id: selectedShippingMethod,
      notes: notes,
    };
    if (!isAuthenticated) {
      orderPayload.guest = {
        name: guestDetails.name.trim(),
        email: guestDetails.email.trim(),
        phone: guestDetails.phone.trim(),
      };
    }

    let orderId = null;
    try {
      const placeRes = await transport.post('/checkout/place-order', orderPayload, {
        headers: { 'Idempotency-Key': idemKey },
        signal,
      });
      if (!mountedRef.current) return;
      if (!placeRes.data?.success) {
        toast.error(placeRes.data?.message || t('checkout.orderError'));
        setIsPlacingOrder(false);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      orderId = placeRes.data.data.id;
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      handlePlaceOrderError(err);
      warnUnloadRef.current = false;
      if (mountedRef.current) setIsPlacingOrder(false);
      return;
    }

    // Step 2 — start payment
    try {
      const startRes = await transport.post('/payments/start', {
        order_id: orderId,
        provider: paymentMethod,
        meta: {
          return_url: `${PUBLIC_URL}/checkout/${orderId}/result`,
          cancel_url: `${PUBLIC_URL}/checkout/${orderId}/result?cancelled=1`,
        },
      }, { signal });
      if (!mountedRef.current) return;

      if (!startRes.data?.success) {
        const gatewayMsg = extractGatewayError(startRes.data);
        trackPaymentFailure('payment_start_failed', {
          orderId,
          provider: paymentMethod,
          reason: gatewayMsg || 'success_false',
        });
        toast.error(gatewayMsg || t('checkout.paymentStartError'));
        // Offer COD as a fallback on this same pending order so the customer
        // doesn't have to redo address+shipping. Only when paymob failed —
        // for COD itself there's nothing to fall back to.
        if (paymentMethod === 'paymob') {
          setGatewayFallbackOrderId(orderId);
        } else {
          navigate(`/checkout/${orderId}/result?failed=1`, { replace: true });
        }
        return;
      }

      const redirectUrl = startRes.data.data?.redirect_url;
      if (redirectUrl) {
        if (!isTrustedRedirect(redirectUrl)) {
          // The order was created and a redirect URL was returned, but it's
          // not a host we recognize. Refuse to navigate — let the user retry
          // from the result page rather than be sent to a stranger domain.
          trackPaymentFailure('payment_redirect_untrusted', {
            orderId,
            provider: paymentMethod,
            reason: redirectUrl,
          });
          toast.error(t('checkout.paymentStartError'));
          navigate(`/checkout/${orderId}/result?failed=1`, { replace: true });
          return;
        }
        // Disable the unload warning synchronously so our own redirect
        // to Paymob doesn't trigger the browser prompt.
        warnUnloadRef.current = false;
        window.location.href = redirectUrl;
        return;
      }

      // No redirect_url: only valid for offline providers (COD).
      if (paymentMethod === 'cod') {
        navigate(`/checkout/${orderId}/result`, { replace: true });
      } else {
        trackPaymentFailure('payment_start_no_redirect', {
          orderId,
          provider: paymentMethod,
          reason: 'redirect_url_null',
        });
        toast.error(t('checkout.paymentStartError'));
        navigate(`/checkout/${orderId}/result?failed=1`, { replace: true });
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      const status = err.response?.status;
      const body = err.response?.data;
      const gatewayMsg = extractGatewayError(body);
      trackPaymentFailure('payment_start_failed', {
        orderId,
        provider: paymentMethod,
        status,
        reason: gatewayMsg || err?.message || 'network',
      });
      if (status === 429) {
        showThrottleToast(err);
      } else {
        toast.error(gatewayMsg || t('checkout.paymentStartError'));
      }
      // For a paymob 422 (gateway not configured / misconfigured) the order
      // is in pending state — keep it and let the customer retry as COD
      // without re-entering address+shipping. For other errors, send them
      // to the result page so they can retry from there.
      if (mountedRef.current) {
        if (paymentMethod === 'paymob' && status === 422) {
          setGatewayFallbackOrderId(orderId);
        } else {
          navigate(`/checkout/${orderId}/result?failed=1`, { replace: true });
        }
      }
    } finally {
      // Clear the unload guard once we've reached a terminal state on this
      // page — we either redirected to Paymob (handled above) or stopped
      // submitting and stayed put / navigated to /result.
      warnUnloadRef.current = false;
      if (mountedRef.current) setIsPlacingOrder(false);
    }
  };

  // Sprint A — retry /payments/start against the existing pending order
  // using COD instead of Paymob. Used by the gateway-failure dialog.
  const handleCodFallback = async () => {
    const orderId = gatewayFallbackOrderId;
    if (!orderId || isFallbackSubmitting) return;
    setIsFallbackSubmitting(true);
    try {
      const startRes = await transport.post('/payments/start', {
        order_id: orderId,
        provider: 'cod',
        meta: {
          return_url: `${PUBLIC_URL}/checkout/${orderId}/result`,
          cancel_url: `${PUBLIC_URL}/checkout/${orderId}/result?cancelled=1`,
        },
      });
      if (!startRes.data?.success) {
        toast.error(extractGatewayError(startRes.data) || t('checkout.paymentStartError'));
        return;
      }
      setGatewayFallbackOrderId(null);
      navigate(`/checkout/${orderId}/result`, { replace: true });
    } catch (err) {
      const body = err.response?.data;
      toast.error(extractGatewayError(body) || t('checkout.paymentStartError'));
    } finally {
      setIsFallbackSubmitting(false);
    }
  };

  const dismissGatewayFallback = () => {
    const orderId = gatewayFallbackOrderId;
    setGatewayFallbackOrderId(null);
    if (orderId) navigate(`/checkout/${orderId}/result?failed=1`, { replace: true });
  };

  const handlePlaceOrderError = (err) => {
    const status = err.response?.status;
    const body = err.response?.data;
    const itemErrors = Array.isArray(body?.errors) ? body.errors : [];

    trackPaymentFailure('place_order_failed', {
      provider: paymentMethod,
      status,
      reason: body?.message || err?.message || 'network',
    });

    if (status === 429) {
      showThrottleToast(err);
      return;
    }

    if (!isAuthenticated && body?.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
      const fields = ['name', 'email', 'phone'];
      const next = {};
      fields.forEach((f) => {
        const key = `guest.${f}`;
        if (body.errors[key]) next[f] = body.errors[key];
      });
      if (Object.keys(next).length > 0) setGuestErrors(next);
    }

    toast.error(body?.message || t('checkout.orderError'));

    itemErrors.forEach((e) => {
      const msg = e?.message || e?.error;
      if (msg) toast.error(msg, { duration: 6000 });
    });

    const localIds = new Set((cart?.items || []).map(it => Number(it.product_id)));
    const ghostItems = itemErrors.filter(e => e?.product_id && !localIds.has(Number(e.product_id)));
    if (ghostItems.length > 0) {
      localStorage.removeItem('cart_session_id');
      toast.error(t('checkout.cartMismatchReset', 'Cart sync issue detected. Refreshing your cart...'), { duration: 6000 });
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      return;
    }

    if (itemErrors.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    }
  };


  // While placing an order we may briefly enter a state where the cart
  // refetch returned null/empty (server cleared it) before we navigate
  // away. Show a busy spinner instead of crashing on cart.* below.
  if (isCartLoading || isAddressLoading || isPlacingOrder) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>{t('cart.empty')}</Typography>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('cart.continueShopping')}
        </Button>
      </Container>
    );
  }

  // Calculations — mirror Cart page: subtotal + tax + shipping − discount
  const subtotal = Number(cart.subtotal) || 0;
  const tax = Number(cart.tax_total ?? cart.tax) || 0;
  const discount = Number(cart.discount_total ?? cart.discount) || 0;
  const appliedCoupon = cart.coupon || null;
  const selectedMethod = shippingMethods.find(m => m.id === selectedShippingMethod);
  const shippingCost = Number(
    selectedMethod?.price ?? selectedMethod?.cost ?? cart.shipping_total ?? cart.shipping ?? 0
  ) || 0;

  const apiTotal = Number(cart.grand_total ?? cart.total);
  const computedTotal = Math.max(0, subtotal + tax + shippingCost - discount);
  // Live total — uses the API value if no shipping change occurred,
  // otherwise computes locally so the UI updates instantly.
  const total = (
    Number.isFinite(apiTotal) && apiTotal > 0 && !selectedMethod
      ? apiTotal
      : computedTotal
  );
  const currency = cart?.currency;

  // Helper to get selected address object
  const selectedAddress = addresses?.find(a => a.id === selectedAddressId);
  const selectedShippingMethodObj = shippingMethods?.find(m => m.id === selectedShippingMethod);

  // Step completion / locked rules — drive the StepButton's
  // disabled state so a step can't be jumped to before its
  // predecessor has the data it needs.
  const stepCompleted = [
    Boolean(selectedAddressId),
    Boolean(selectedShippingMethod),
    Boolean(paymentMethod),
  ];
  const canEnterStep = (idx) => {
    if (idx === 0) return true;
    if (idx === 1) return stepCompleted[0];
    if (idx === 2) return stepCompleted[0] && stepCompleted[1];
    return false;
  };

  // Forward navigation goes through handleNextToShipping /
  // handleNextToPayment (they validate before advancing); the Back
  // buttons just decrement so we don't need a generic `goNext`.
  const goBack = () => setActiveStep((s) => Math.max(0, s - 1));

  return (
    <Container maxWidth="xl" sx={{ py: 4, overflowX: 'hidden' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        {t('checkout.title')}
      </Typography>

      <Box
        sx={{
          mb: 4,
          // RTL-safe stepper: MUI's `alternativeLabel` connector uses
          // physical `left`/`right` offsets which break in RTL because
          // the connector is positioned relative to its own Step (DOM
          // order) but the visual order is reversed. Use logical
          // inset-inline so the line lands on the correct side in
          // both directions.
          '& .MuiStepConnector-root.MuiStepConnector-alternativeLabel': {
            top: 22,
            left: 'auto',
            right: 'auto',
            insetInlineStart: 'calc(-50% + 22px)',
            insetInlineEnd: 'calc(50% + 22px)',
          },
          '& .MuiStepConnector-line': {
            borderTopWidth: 2,
          },
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel nonLinear>
          <Step completed={stepCompleted[0]}>
            <StepButton onClick={() => setActiveStep(0)}>
              <StepLabel>{t('checkout.shippingAddress', 'Shipping')}</StepLabel>
            </StepButton>
          </Step>
          <Step completed={stepCompleted[1]}>
            <StepButton
              onClick={() => canEnterStep(1) && setActiveStep(1)}
              disabled={!canEnterStep(1)}
            >
              <StepLabel>{t('checkout.shippingMethod', 'Method')}</StepLabel>
            </StepButton>
          </Step>
          <Step completed={stepCompleted[2]}>
            <StepButton
              onClick={() => canEnterStep(2) && setActiveStep(2)}
              disabled={!canEnterStep(2)}
            >
              <StepLabel>{t('checkout.paymentReview', 'Payment & review')}</StepLabel>
            </StepButton>
          </Step>
        </Stepper>
      </Box>

      <Grid container spacing={4}>
        {/* LEFT COLUMN: ACCORDIONS */}
        <Grid item xs={12} md={7} lg={8}>

          {/* GUEST DETAILS — only when unauthenticated. The backend
              creates a silent User row from these on place-order. */}
          {!isAuthenticated && (
            <GuestDetailsCard
              values={guestDetails}
              errors={guestErrors}
              onChange={(next) => {
                setGuestDetails(next);
                setGuestErrors({});
              }}
            />
          )}

          {/* SECTION 1: ADDRESS */}
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
            <Accordion
              expanded={expanded === 'address'}
              onChange={handleAccordionChange('address')}
              elevation={0}
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <LocationOn color={selectedAddressId ? 'primary' : 'disabled'} />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold">{t('checkout.shippingAddress')}</Typography>
                    {!expanded === 'address' && selectedAddress && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {selectedAddress.name}, {selectedAddress.area}, {selectedAddress.block}
                      </Typography>
                    )}
                  </Box>
                  {expanded !== 'address' && selectedAddressId && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={(e) => { e.stopPropagation(); setActiveStep(0); }}
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">{t('checkout.chooseShippingAddress', 'Choose a shipping address')}</Typography>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    size="small"
                    onClick={() => setShowAddressForm(true)}
                  >
                    {t('profile.addAddress', 'Add New Address')}
                  </Button>
                </Box>

                <AddressesList
                  addresses={addresses}
                  selectable
                  selectedId={selectedAddressId}
                  onSelect={setSelectedAddressId}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant="contained" onClick={handleNextToShipping}>
                    {t('common.continue', 'Continue')}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* SECTION 2: SHIPPING METHOD */}
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
            <Accordion
              expanded={expanded === 'shipping'}
              onChange={handleAccordionChange('shipping')}
              disabled={!selectedAddressId}
              elevation={0}
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <LocalShipping color={selectedShippingMethod ? 'primary' : 'disabled'} />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold">{t('checkout.shippingMethod')}</Typography>
                    {!expanded === 'shipping' && selectedShippingMethodObj && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedShippingMethodObj.name[i18n.language] || selectedShippingMethodObj.name['en']} ({Number(selectedShippingMethodObj.price) === 0 ? t('common.free') : formatMoney(selectedShippingMethodObj.price, currency)})
                      </Typography>
                    )}
                  </Box>
                  {expanded !== 'shipping' && selectedShippingMethod && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={(e) => { e.stopPropagation(); setActiveStep(1); }}
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                {shippingMethods.length === 0 ? (
                  <Alert severity="warning">{t('checkout.noShippingMethods', 'No shipping methods available for this address.')}</Alert>
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={selectedShippingMethod}
                      onChange={(e) => setSelectedShippingMethod(Number(e.target.value))}
                    >
                      {shippingMethods.map((method) => (
                        <Paper
                          key={method.id}
                          variant="outlined"
                          sx={{
                            mb: 1,
                            p: 1.5,
                            borderColor: selectedShippingMethod === method.id ? 'primary.main' : 'divider',
                            backgroundColor: selectedShippingMethod === method.id ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent'
                          }}
                        >
                          <FormControlLabel
                            value={method.id}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {method.name[i18n.language] || method.name['en'] || method.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {Number(method.price || method.cost) === 0 ? t('common.free', 'Free') : formatMoney(method.price || method.cost, currency)}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                  <Button variant="text" startIcon={<ArrowBack />} onClick={goBack}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={handleNextToPayment}
                    disabled={!selectedShippingMethod}
                  >
                    {t('common.continue', 'Continue')}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* SECTION 3: PAYMENT */}
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
            <Accordion
              expanded={expanded === 'payment'}
              onChange={handleAccordionChange('payment')}
              disabled={!selectedShippingMethod}
              elevation={0}
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Payment color={paymentMethod ? 'primary' : 'disabled'} />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold">{t('checkout.paymentMethod')}</Typography>
                    {expanded !== 'payment' && (
                      <Typography variant="body2" color="text.secondary">
                        {paymentMethod === 'paymob' ? t('checkout.paymob') : t('checkout.cashOnDelivery')}
                      </Typography>
                    )}
                  </Box>
                  {expanded !== 'payment' && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={(e) => { e.stopPropagation(); setActiveStep(2); }}
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderColor: paymentMethod === 'paymob' ? 'primary.main' : 'divider',
                        backgroundColor: paymentMethod === 'paymob' ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent'
                      }}
                    >
                      <FormControlLabel
                        value="paymob"
                        control={<Radio />}
                        label={t('checkout.paymob', 'الدفع الإلكتروني (Paymob)')}
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderColor: paymentMethod === 'cod' ? 'primary.main' : 'divider',
                        backgroundColor: paymentMethod === 'cod' ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent'
                      }}
                    >
                      <FormControlLabel
                        value="cod"
                        control={<Radio />}
                        label={t('checkout.cashOnDelivery', 'Cash on Delivery')}
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  </RadioGroup>
                </FormControl>

                {/* PLACE ORDER BUTTON HERE FOR THE FINAL STEP */}
                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" justifyContent="flex-start" sx={{ mb: 1.5 }}>
                    <Button variant="text" startIcon={<ArrowBack />} onClick={goBack}>
                      {t('common.back', 'Back')}
                    </Button>
                  </Stack>
                  <Btn
                    onClick={handlePlaceOrder}
                    loading={isPlacingOrder}
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<CheckCircle />}
                    sx={{ py: 1.5, fontSize: '1.1rem' }}
                  >
                    {t('checkout.placeOrder')}
                  </Btn>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                    {t('checkout.reviewOrderMessage', 'By clicking place order you agree to our terms.')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.75, display: 'block' }}>
                    {t(
                      'checkout.confirmationEmailNotice',
                      'A confirmation email will be sent automatically.'
                    )}
                  </Typography>
                </Box>

              </AccordionDetails>
            </Accordion>
          </Paper>

        </Grid>

        {/* RIGHT COLUMN: SUMMARY */}
        <Grid item xs={12} md={5} lg={4}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 3,
              position: 'sticky',
              top: 24,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
              borderRadius: 3
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {t('checkout.orderSummary')}
            </Typography>

            <Box sx={{ maxHeight: '40vh', overflowY: 'auto', mb: 3, pr: 1 }}>
              {cart.items.map(item => (
                <Box key={item.id} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box
                    component="img"
                    src={item.product.cover_url}
                    alt={item.product.name?.en}
                    sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, bgcolor: 'background.default' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {item.product.name?.[i18n.language] || item.product.name?.['en'] || item.product.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.variant?.variant_key || item.variant_name}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption">{t('cart.qty', 'Qty')}: {item.qty}</Typography>
                      <Typography variant="body2" fontWeight="medium">{formatMoney(item.line_total, currency)}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">{t('cart.subtotal')}</Typography>
              <Typography fontWeight="medium">{formatMoney(subtotal, currency)}</Typography>
            </Box>

            {tax > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">{t('cart.tax')}</Typography>
                <Typography fontWeight="medium">{formatMoney(tax, currency)}</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">{t('cart.shipping')}</Typography>
              <Typography fontWeight="medium" color={shippingCost === 0 ? 'success.main' : undefined}>
                {shippingCost === 0 ? t('common.free', 'Free') : formatMoney(shippingCost, currency)}
              </Typography>
            </Box>

            {discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">
                  {appliedCoupon?.code
                    ? `${t('cart.discount', 'Discount')} (${appliedCoupon.code})`
                    : t('cart.discount', 'Discount')}
                </Typography>
                <Typography fontWeight="medium" color="success.main">
                  − {formatMoney(discount, currency)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">{t('cart.total')}</Typography>
              <Box textAlign="right">
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {formatMoney(total, currency)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

      </Grid>

      {/* Sprint A — Gateway failure fallback dialog. Shown when /payments/start
          returns success:false or 422 for paymob; lets the customer pivot to
          COD on the same pending order without redoing address+shipping. */}
      <Dialog
        open={Boolean(gatewayFallbackOrderId)}
        onClose={isFallbackSubmitting ? undefined : dismissGatewayFallback}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('checkout.gatewayFallbackTitle', 'Online payment unavailable')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'checkout.gatewayFallbackBody',
              "We couldn't start the online payment right now. Your order is saved — would you like to pay on delivery instead?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissGatewayFallback} disabled={isFallbackSubmitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCodFallback}
            variant="contained"
            disabled={isFallbackSubmitting}
          >
            {isFallbackSubmitting
              ? t('common.loading', 'Loading...')
              : t('checkout.payOnDelivery', 'Pay on Delivery')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Form Dialog */}
      <Dialog open={showAddressForm} onClose={() => setShowAddressForm(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <AddressForm
            onSubmit={handleAddressCreate}
            onCancel={() => setShowAddressForm(false)}
            loading={createAddressHook.isPending}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Checkout;
